import { Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import UploadImage from 'src/pages/new-post-new/uploadFile';
import { CommonHelper } from 'src/utils/commonHelper';
import LinearProgressBar from '../progress-bar/progressBar.component';

const ImageEditor = ({
  showToast,
  translate,
  handleUploadEditedImage,
  handleCloseImageEditor,
  theme,
  imageUrl = '',
}) => {
  const [originalImage, setOriginalImage] = useState(imageUrl);
  const [fileUploadprogress, setFileUploadprogress] = useState(0);

  const handleImageUpload = useCallback((imageData) => {
    setOriginalImage(imageData);
  }, []);

  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const fileUploadSuccessCallback = (data, message) => {
    const mFiles = [...data];
    message && showToast(message, 's');
    handleUploadEditedImage(mFiles);
  };

  const uploadMediaFilesToServer = useCallback((file) => {
    let uploadFilesProps = {
      files: [file],
      setFileUploadprogress,
      successCallback: fileUploadSuccessCallback,
      failureCallback,
    };
    CommonHelper.UploadMediaFilesToServer({ ...uploadFilesProps });
  }, []);

  const handleSave = useCallback(
    (editedImageObject, designState) => {
      try {
        editedImageObject.imageCanvas.toBlob(
          (blob) => {
            if (!blob) {
              showToast('Failed to create image file.', 'e');
              return;
            }

            const file = new File([blob], `edited-image-${Date.now()}.png`, {
              type: 'image/png',
              lastModified: Date.now(),
            });

            uploadMediaFilesToServer(file);
          },
          'image/png',
          1.0
        );
      } catch (err) {
        console.error('Error saving edited image:', err);
        showToast('Failed to upload edited image. Please try again.', 'e');
      }
    },
    [handleUploadEditedImage, handleCloseImageEditor, showToast]
  );

  const handleImages = (files) => {
    const imageFile = files[0];
    if (CommonHelper.SupportedImageFormat.some((a) => a === imageFile.type)) {
      if (imageFile.size <= CommonHelper.MaximumUploadImageSize) {
        const reader = new FileReader();

        reader.onload = (e) => {
          const imageDataUrl = e.target.result;
          handleImageUpload(imageDataUrl);
        };

        reader.onerror = () => {
          showToast('Failed to read the selected file. Please try again.', 'e');
        };

        reader.readAsDataURL(imageFile);
      } else {
        showToast(
          `${translate('media_library.max_file_size')} 50MB ${translate('media_library.select_below_max_size')} 50MB`,
          'e'
        );
      }
    } else {
      showToast(`${translate('common.we_only_support')} JPEG, JPG, PNG ${translate('common.file_formats')}`, 'e');
    }
  };

  return (
    <>
      <div style={{ flex: 1 }}>{fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}</div>
      {originalImage ? (
        <FilerobotImageEditor
          source={originalImage}
          onSave={handleSave}
          onClose={handleCloseImageEditor}
          annotationsCommon={{
            fill: '#ff0000',
          }}
          Text={{
            fill: '#ffffff',
            fontFamily: 'Arial',
            fontSize: 24,
          }}
          Rotate={{
            angle: 90,
            componentType: 'slider',
          }}
          tabsIds={[TABS.ADJUST, TABS.EFFECTS, TABS.FILTERS, TABS.FINETUNE, TABS.RESIZE, TABS.WATERMARK, TABS.ANNOTATE]}
          defaultTabId={TABS.ADJUST}
          defaultToolId={TOOLS.CROP}
          savingPixelRatio={1}
          previewPixelRatio={1}
          theme={{
            palette: {
              'bg-primary': '#ffffff',
              'bg-secondary': '#f8f9fa',
              'accent-primary': theme.palette.primary.main,
              'accent-primary-active': theme.palette.primary.dark,
            },
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            borderRadius: 2,
            border: '2px dashed #ccc',
            mx: 2,
            my: 2,
            height: '100%',
          }}
        >
          <Typography variant="h6" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
            {"Upload an image to start editing"}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <UploadImage name="images" accept={['image/*']} onDrop={handleImages} customSupportedFilesText={"Only JPEG, PNG files are supported."} />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ImageEditor;
